import type { DependencyList } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Crop, PixelCrop } from 'react-image-crop';
import ReactCrop, { convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { FilePicker } from './FilePicker';
import { centerAspectCrop } from './utils';
import styles from './style/ImageCropper.module.css';

// Workaround: Static CSS import on line 5 doesn't work due to build config issue
// Loading from CDN until bundler configuration is fixed
if (typeof window !== 'undefined') {
  const style = document.createElement('link');
  style.rel = 'stylesheet';
  style.href = 'https://unpkg.com/react-image-crop@10.0.0/dist/ReactCrop.css';
  if (!document.querySelector(`link[href="${style.href}"]`)) {
    document.head.appendChild(style);
  }
}

type Props = {
  aspectRatio?: number;
  isSaving?: boolean;
  onSave: (data: { blob: Blob }) => unknown;
  src?: string;
  strictAspectRatio?: boolean;
  onFileSelect?: (file: File) => void;
  fileError?: string;
};

export const ImageCropper = ({
  aspectRatio = 1,
  onSave,
  isSaving,
  src = '',
  strictAspectRatio = true,
  onFileSelect,
  fileError,
}: Props) => {
  const preloadedRef = useRef(false);
  const [imgSrc, setImgSrc] = useState('');
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const blobUrlRef = useRef('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, _setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(aspectRatio);

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      onFileSelect?.(file);

      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(file);
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      const params = centerAspectCrop(width, height, aspect);
      setCrop(params);
    }
  }

  async function onDownloadCropClick() {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const offscreen = new OffscreenCanvas(completedCrop.width * scaleX, completedCrop.height * scaleY);
    const ctx = offscreen.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );
    const blob = await offscreen.convertToBlob({
      type: 'image/png',
    });

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    blobUrlRef.current = URL.createObjectURL(blob);

    return onSave({ blob });
  }

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  useEffect(() => {
    if (preloadedRef.current || !src) return;

    if (!imgRef.current && !imgSrc) {
      const img = new Image();
      img.onload = () => {
        if (!imgSrc) {
          setImgSrc(src);
        }
      };
      img.src = src;
      return;
    }

    if (!imgRef.current) return;

    if (!previewCanvasRef.current && !completedCrop) {
      const { width, height } = imgRef.current;
      const newCrop = centerAspectCrop(width, height, aspectRatio);
      setCrop(newCrop);
      setCompletedCrop(convertToPixelCrop(newCrop, width, height));
      return;
    }

    if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
      preloadedRef.current = true;
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
    }
  }, [completedCrop, imgSrc, scale, rotate, src, aspectRatio]);

  function handleToggleAspectClick() {
    if (aspect) {
      setAspect(undefined);
    } else {
      setAspect(aspectRatio);

      if (imgRef.current) {
        const { width, height } = imgRef.current;
        const newCrop = centerAspectCrop(width, height, aspectRatio);
        setCrop(newCrop);
        setCompletedCrop(convertToPixelCrop(newCrop, width, height));
      }
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.cropControls}>
        <div className={styles.tools}>
          <FilePicker onSelect={onSelectFile} />
          <div className={styles.edit}>
            <div>
              <label htmlFor="scale-input">Scale: </label>
              <input
                className={styles.scale}
                id="scale-input"
                type="number"
                step="0.1"
                value={scale}
                disabled={!imgSrc}
                onChange={(e) => setScale(Number(e.target.value))}
              />
            </div>
            {!strictAspectRatio && (
              <div>
                <button className={styles.aspect} onClick={handleToggleAspectClick}>
                  {aspect ? `Unlock aspect ratio` : `Lock aspect ratio`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {fileError && (
        <div className={styles.error} role="alert">
          {fileError}
        </div>
      )}

      {!!imgSrc && (
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspect}
          minHeight={100}
        >
          <img
            ref={imgRef}
            alt="Cropper"
            src={imgSrc}
            style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      )}

      {!!completedCrop && (
        <div className={styles.previewContainer}>
          <canvas
            ref={previewCanvasRef}
            style={{
              border: '1px solid black',
              objectFit: 'contain',
              width: Math.min(completedCrop.width, 120),
              height: Math.min(completedCrop.height, 120),
            }}
          />
        </div>
      )}

      <div className={styles.footer}>
        <Button.Primary
          className={styles.btn}
          variant="brick"
          disabled={!completedCrop || isSaving}
          implicitDisable={false}
          onClick={onDownloadCropClick}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button.Primary>
      </div>
    </div>
  );
};

// Canvas preview helper
const TO_RADIANS = Math.PI / 180;

async function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();
}

// Debounce hook
function useDebounceEffect(fn: () => void, waitTime: number, deps?: DependencyList) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn();
    }, waitTime);

    return () => clearTimeout(t);
  }, deps);
}
